import { useState, useRef } from "react";
import LongAnswerInput from "../Input/LongAnswerInput";
import CustomButton from "../Input/CustomButton";
import TextYScroller from "./textYScroller";
import LongFormSkeleton from "../Skeletons/LongFormSkeleton";
import {
	useNotes,
	useAddNote,
	useDeleteNote
} from "../../../services/api-client/useNotes";
import { useSnackbar } from "../../../services/api-client/useSnackbar";
import { formatEventTimestamp } from "../../../Utilities/FormatEventTimestamp";
import { TrashIcon } from "../Icons/Interface";

interface NotesTabProps {
	jobId: string;
}

const NotesTab: React.FC<NotesTabProps> = ({ jobId }) => {
	const noteTextRef = useRef("");
	const [hasNoteText, setHasNoteText] = useState(false);
	const [noteKey, setNoteKey] = useState(0);
	const ctrlEnterRef = useRef(false);

	const { notes, isLoading } = useNotes(jobId);
	const addNote = useAddNote(jobId);
	const deleteNote = useDeleteNote(jobId);
	const { showSnack } = useSnackbar();

	const handleDeleteNote = async (noteId: number) => {
		await deleteNote.mutateAsync(noteId);
		showSnack("NotifyNoteDeleted", "dark");
	};

	const saveNote = async (text?: string) => {
		const noteText = text ?? noteTextRef.current;
		if (!noteText.trim()) return;
		await addNote.mutateAsync({ text: noteText.trim() });
		noteTextRef.current = "";
		setHasNoteText(false);
		setNoteKey((k) => k + 1);
		showSnack("NotifyNoteSaved", "dark");
	};

	if (isLoading) {
		return <LongFormSkeleton className="min-h-96" />;
	}

	return (
		<div className="space-y-4">
			<div
				onKeyDownCapture={(e) => {
					if (e.ctrlKey && e.key === "Enter") {
						ctrlEnterRef.current = true;
					}
				}}
			>
				<LongAnswerInput
					key={noteKey}
					label="Add a note..."
					enableAudio={false}
					onTextChange={(text) => {
						noteTextRef.current = text;
						setHasNoteText(!!text.trim());
					}}
					onTextComplete={async (text) => {
						noteTextRef.current = text;
						setHasNoteText(!!text.trim());
						if (ctrlEnterRef.current) {
							ctrlEnterRef.current = false;
							await saveNote(text);
						}
					}}
				/>
			</div>

			<div className="flex justify-end">
				<CustomButton
					text="Save Note"
					rightIcon="External"
					disabled={!hasNoteText}
					onClick={() => saveNote()}
				/>
			</div>

			<TextYScroller
				tailClassMinHeight="min-h-0"
				tailClassMaxHeight="max-h-[calc(100vh-500px)]"
			>
				{notes.map((note, index) => (
					<div
						key={note.eventId ?? index}
						className="py-3 border-b border-primary-accent flex items-start justify-between gap-3"
					>
						<div className="flex-1 min-w-0">
							<p className="text-sm text-primary whitespace-pre-wrap">
								{note.text}
							</p>
							<p className="text-xs text-primary mt-1">
								{formatEventTimestamp(note.eventTimestamp)}
							</p>
						</div>
						<button
							type="button"
							aria-label="Delete note"
							onClick={() => handleDeleteNote(note.noteId)}
							disabled={deleteNote.isPending}
							className="text-primary-inactive hover:text-error-active transition-colors p-1 flex-shrink-0 disabled:opacity-50"
						>
							{TrashIcon()}
						</button>
					</div>
				))}
			</TextYScroller>
		</div>
	);
};

export default NotesTab;
